import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversationsStore, useUIStore } from '@/stores';
import { useHashNavigate } from '@/contexts/HashRouterContext';
import { showToast } from '@/components/Toast';

interface UseSidebarHandlersOptions {
  createNewConversation: () => void;
  updateConversation: (
    id: string,
    method: 'PUT' | 'DELETE',
    title?: string
  ) => void;
  selectProject: (projectId: string | null) => void;
  setChatReadOnly: (readOnly: boolean) => void;
  setSharedConversationRecordId: (id: number | null) => void;
}

export function useSidebarHandlers({
  createNewConversation,
  updateConversation,
  selectProject,
  setChatReadOnly,
  setSharedConversationRecordId,
}: UseSidebarHandlersOptions) {
  const { t } = useTranslation();
  const { setChatPageSelectedConversationId } = useConversationsStore();
  const { setSelectedAgent } = useUIStore();
  const navigate = useHashNavigate();

  // Suppress unused variable warnings - kept for interface compatibility
  void selectProject;

  const handleConversationSelect = useCallback(
    (id: string) => {
      setChatReadOnly(false);
      setSharedConversationRecordId(null);
      navigate({ type: 'chat', conversationId: id });
    },
    [setChatReadOnly, setSharedConversationRecordId, navigate]
  );

  const handleNewConversation = useCallback(() => {
    createNewConversation();
    setSelectedAgent(null);
    setChatReadOnly(false);
    setSharedConversationRecordId(null);
    navigate({ type: 'chat' });
  }, [
    createNewConversation,
    setSelectedAgent,
    setChatReadOnly,
    setSharedConversationRecordId,
    navigate,
  ]);

  const handleUpdateConversation = useCallback(
    async (id: string, method: string, title?: string) => {
      const res: any = await updateConversation(
        id,
        method as 'PUT' | 'DELETE',
        title
      );
      if (method === 'DELETE' && res?.code === 200) {
        const { chatPageSelectedConversationId, setConversations } =
          useConversationsStore.getState();
        if (chatPageSelectedConversationId === id) {
          setChatPageSelectedConversationId(undefined);
          // Navigate away to remove conversationId from route
          navigate({ type: 'chat' });
        }
        // 🔥 更新全局 store，确保所有页面同步
        setConversations(prev =>
          prev.filter(conv => conv.id !== id && conv.conversationId !== id)
        );
        // 🔥 刷新侧边栏 Starred 和 Recent chats 列表
        if (window.refreshSidebarStarred) {
          window.refreshSidebarStarred();
        }
        if (window.refreshSidebarRecent) {
          window.refreshSidebarRecent();
        }
        // 🔥 同步 ChatsPage 的本地 conversations 状态
        if (window.removeConversationFromPages) {
          window.removeConversationFromPages(id);
        }
      }
      return res;
    },
    [updateConversation, setChatPageSelectedConversationId, navigate]
  );

  const handleRenameConversation = useCallback(
    async (id: string, title: string) => {
      const res: any = await updateConversation(id, 'PUT', title);
      if (res?.code === 200) {
        showToast(t('toast.conversationRenamedSuccessfully'), 'success', {
          title: t('common.success'),
        });
        // 🔥 更新全局 store，确保所有页面同步
        const { setConversations } = useConversationsStore.getState();
        setConversations(prev =>
          prev.map(conv =>
            conv.id === id || conv.conversationId === id
              ? { ...conv, title: title.trim() }
              : conv
          )
        );
        // 🔥 刷新侧边栏 Starred 和 Recent chats 列表
        if (window.refreshSidebarStarred) {
          window.refreshSidebarStarred();
        }
        if (window.refreshSidebarRecent) {
          window.refreshSidebarRecent();
        }
        // 🔥 同步 ChatsPage 的本地 conversations 状态
        if (window.updateConversationTitleInPages) {
          window.updateConversationTitleInPages(id, title.trim());
        }
      } else {
        showToast(res?.message || 'Failed to rename conversation', 'error', {
          title: 'Error',
        });
      }
      return res;
    },
    [updateConversation, t]
  );

  return {
    handleConversationSelect,
    handleNewConversation,
    handleUpdateConversation,
    handleRenameConversation,
  };
}
